import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { pushEvent } from '../../utils/analytics';
import { getApiBaseUrl } from '../../utils/apiBaseUrl';
import useABTest from '../../hooks/useABTest';
import { ACTIVE_EXPERIMENTS } from '../../utils/experiments';
import {
  buildChallengeCalendarLinks,
  normalizeChallengeSlug,
  formatChallengeDateTime,
  pickKickoffAndDemo,
  youtubeLiveUrl,
} from './challengePageUtils';
import './ChallengePageClarity.css';
import './ChallengeBookingPage.css';

const CALENDLY_URL =
  'https://calendly.com/lumiwealth/botspot-call?hide_event_type_details=1&hide_gdpr_banner=1';

const readBookingContext = (slug) => {
  try {
    const saved = JSON.parse(window.sessionStorage.getItem('challengeBookingContext') || '{}');
    return saved?.slug === slug ? saved : {};
  } catch {
    return {};
  }
};

export default function ChallengeBookingPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const widgetRef = useRef(null);
  const detailsRef = useRef(null);
  const context = useMemo(
    () =>
      location.state?.bookingContext?.slug === slug
        ? location.state.bookingContext
        : readBookingContext(slug),
    [slug, location.state],
  );
  const completedEvents = useRef(new Set());
  const [widgetError, setWidgetError] = useState(false);
  const [challenge, setChallenge] = useState(null);
  const [scheduleError, setScheduleError] = useState(false);
  useEffect(() => {
    // Existing email/calendar links must still open this exact cohort's details.
    if (location.hash === '#event-details' && detailsRef.current) {
      detailsRef.current.open = true;
    }
  }, [location.hash]);

  const bookingExperiment = ACTIVE_EXPERIMENTS.CHALLENGE_BOOKING_SETUP_VS_PROJECT;
  const { isTest: isProjectVariant } = useABTest(
    bookingExperiment.name,
    bookingExperiment.featureFlag,
    'control',
    {
      page: 'challenge_booking',
      registration_id: context.registrationId || undefined,
      validVariants: ['control', 'test'],
      normalizeControlExposure: true,
    },
  );

  useEffect(() => {
    let mounted = true;
    const apiUrl = getApiBaseUrl() || 'https://api.botspot.trade';
    const abort = new AbortController();
    const deadline = window.setTimeout(() => {
      abort.abort();
      if (mounted) setScheduleError(true);
    }, 20000);

    (async () => {
      try {
        let match;
        let page = 1;
        let pages = 1;
        do {
          const listResponse = await fetch(
            `${apiUrl}/courses/session-groups?active=false&registrationAccess=public&limit=100&page=${page}`,
            { signal: abort.signal },
          );
          if (!listResponse.ok) throw new Error(`session_groups_${listResponse.status}`);
          const listPayload = await listResponse.json();
          const groups = Array.isArray(listPayload)
            ? listPayload
            : listPayload.sessionGroups || listPayload.data || [];
          // An old email's details must never roll forward to a different run.
          match = groups.find(
            (group) => normalizeChallengeSlug(group.name) === normalizeChallengeSlug(slug),
          );
          pages = Number(listPayload.pagination?.pages) || 1;
          page += 1;
        } while (!match && page <= pages && !abort.signal.aborted);
        if (!match) throw new Error('challenge_not_found');

        const detailResponse = await fetch(`${apiUrl}/courses/session-groups/${match.id}`, {
          signal: abort.signal,
        });
        if (!detailResponse.ok) throw new Error('challenge_details_unavailable');
        const detail = await detailResponse.json();
        if (normalizeChallengeSlug(detail.name) !== normalizeChallengeSlug(slug))
          throw new Error('challenge_identity_mismatch');
        if (!mounted) return;
        setChallenge({ ...match, ...detail });
        setScheduleError(false);
      } catch {
        if (!mounted) return;
        setScheduleError(true);
      } finally {
        window.clearTimeout(deadline);
      }
    })();

    return () => {
      mounted = false;
      abort.abort();
      window.clearTimeout(deadline);
    };
  }, [slug]);

  useEffect(() => {
    pushEvent({
      event: 'challenge_booking_page_viewed',
      source: window.location.pathname,
      registration_id: context.registrationId || undefined,
    });
  }, [context.registrationId]);

  useEffect(() => {
    const mount = widgetRef.current;
    let active = true;
    let initialized = false;

    const initialize = () => {
      if (!active || initialized || !window.Calendly || !widgetRef.current) return;
      initialized = true;
      try {
        window.Calendly.initInlineWidget({
          url: CALENDLY_URL,
          parentElement: mount,
          prefill: {
            email: context.email || '',
            name: context.name || '',
          },
          utm: {
            utmSource: 'ai_trading_challenge',
            utmMedium: 'post_registration',
            utmCampaign: slug,
            utmContent: context.registrationId || '',
            utmTerm: context.bookingReference?.attemptId || '',
          },
        });
      } catch {
        setWidgetError(true);
        return;
      }
      pushEvent({
        event: 'calendly_widget_loaded',
        source: window.location.pathname,
        widget_url: CALENDLY_URL,
      });
    };

    const failed = () => {
      if (active) setWidgetError(true);
    };
    let script;
    const existingScript = document.querySelector('script[data-challenge-calendly="true"]');
    if (existingScript) {
      script = existingScript;
      initialize();
      existingScript.addEventListener('load', initialize);
    } else {
      script = document.createElement('script');
      script.src = 'https://assets.calendly.com/assets/external/widget.js';
      script.async = true;
      script.dataset.challengeCalendly = 'true';
      script.addEventListener('load', initialize);
      document.head.appendChild(script);
    }

    const handleCalendlyMessage = (event) => {
      if (event.origin !== 'https://calendly.com') return;
      if (
        !event.source ||
        !Array.from(mount?.querySelectorAll('iframe') || []).some(
          (frame) => frame.contentWindow === event.source,
        )
      )
        return;
      if (event.data?.event !== 'calendly.event_scheduled') return;
      const inviteeUri = event.data?.payload?.invitee?.uri || null;
      const eventUri = event.data?.payload?.event?.uri || null;
      const inviteeId = inviteeUri?.split('/').filter(Boolean).pop() || null;
      const eventId = inviteeId ? `calendly-${inviteeId}` : null;
      if (!/^https:\/\/api\.calendly\.com\/scheduled_events\/[a-zA-Z0-9-]+$/.test(eventUri || ''))
        return;
      if (
        inviteeUri !== `${eventUri}/invitees/${inviteeId}` ||
        !/^[a-zA-Z0-9-]+$/.test(inviteeId || '')
      )
        return;
      if (completedEvents.current.has(eventId)) return;
      completedEvents.current.add(eventId);
      try {
        window.sessionStorage.setItem(
          'challengeCallContext',
          JSON.stringify({
            inviteeUri,
            eventUri,
            slug,
            readToken: context.bookingReference?.readToken || null,
          }),
        );
      } catch {
        /* Storage denial must not prevent the booking handoff. */
      }
      pushEvent({
        event: 'expert_call_booked',
        source: window.location.pathname,
        calendly_event_type: 'botspot-call',
        registration_id: context.registrationId || undefined,
        event_id: eventId || undefined,
        transaction_id: eventId || undefined,
      });
      navigate(`/challenges/${slug}/call-confirmed`, {
        state: {
          callContext: {
            slug,
            inviteeUri,
            eventUri,
            readToken: context.bookingReference?.readToken || null,
          },
        },
      });
    };

    script.addEventListener('error', failed);
    window.addEventListener('message', handleCalendlyMessage);
    initialize();

    return () => {
      active = false;
      window.removeEventListener('message', handleCalendlyMessage);
      script.removeEventListener('load', initialize);
      script.removeEventListener('error', failed);
      mount?.replaceChildren();
    };
  }, [
    context.email,
    context.name,
    context.registrationId,
    context.bookingReference,
    navigate,
    slug,
  ]);

  const { kickoff, demoDay } = useMemo(
    () => pickKickoffAndDemo(challenge?.sessions || []),
    [challenge],
  );
  const apiUrl = getApiBaseUrl() || 'https://api.botspot.trade';

  const sessions = [kickoff, demoDay].filter(Boolean);
  const callHeadline = isProjectVariant
    ? 'Plan your first AI trading project.'
    : 'Get your trading setup ready.';
  const callCta = isProjectVariant ? 'Plan my project with the team' : 'Book my free setup call';
  const fallbackUrl = new URL(CALENDLY_URL);
  fallbackUrl.searchParams.set('utm_source', 'ai_trading_challenge');
  fallbackUrl.searchParams.set('utm_medium', 'post_registration');
  fallbackUrl.searchParams.set('utm_campaign', slug);
  if (context.registrationId) fallbackUrl.searchParams.set('utm_content', context.registrationId);
  if (context.bookingReference?.attemptId)
    fallbackUrl.searchParams.set('utm_term', context.bookingReference.attemptId);

  return (
    <main
      className="challenge-next challenge-next--welcome challenge-clarity-review"
      data-testid="challenge-booking-page"
      data-education-design-variant="clarity"
      data-education-theme="light"
      data-experiment-active="false"
    >
      <Helmet>
        <title>Your AI Trading Challenge | BotSpot</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <div className="challenge-next__booking-layout">
        <section className="challenge-next__hero">
          <div className="challenge-next__intro">
            <p className="challenge-next__registration">
              {context.registrationId
                ? 'Your challenge seat is saved'
                : 'Free AI Trading Challenge'}
            </p>
            <div className="challenge-next__visual-heading">
              <h1>{callHeadline}</h1>
              <img
                className="challenge-next__setup-art"
                src="/images/challenges/setup-workspace.png"
                alt="A research notebook and laptop, ready for your project"
                width="1024"
                height="1024"
              />
            </div>
            <p className="challenge-next__call-benefit">
              Bring your idea. Leave with a clear starting point.
            </p>
            <p className="challenge-next__call-facts">
              <strong>Free 30-minute call</strong>
              <span>With the challenge team</span>
            </p>
            <div className="challenge-next__actions">
              <a
                className="challenge-next__primary"
                href="#book-call"
                onClick={() =>
                  pushEvent({
                    event: 'challenge_setup_call_clicked',
                    source: window.location.pathname,
                    registration_id: context.registrationId || undefined,
                    variant: isProjectVariant ? 'test' : 'control',
                  })
                }
              >
                {callCta}
              </a>
            </div>
            <p className="challenge-next__expectations">Optional. No purchase required.</p>
          </div>
        </section>
        <div className="challenge-next__booking-tool">
          <section
            id="book-call"
            className="challenge-next__calendar"
            aria-label="Book a free call with the challenge team"
          >
            <div
              ref={widgetRef}
              className="challenge-next__calendly-mount"
              data-testid="challenge-calendly"
            />
          </section>
          <p className="challenge-next__call-disclosure">
            We can also discuss whether the paid Bootcamp fits your goals.
          </p>
          <p className="challenge-next__scheduler-fallback">
            {widgetError ? 'The scheduler could not load. ' : 'Prefer a separate window? '}
            <a href={fallbackUrl.toString()} target="_blank" rel="noopener noreferrer">
              Open the booking calendar
            </a>
          </p>
        </div>
      </div>
      <details
        ref={detailsRef}
        id="event-details"
        className="challenge-next__block challenge-next__session-details"
      >
        <summary>
          <span>
            Live session details
            <small>Your two free workshops, calendar &amp; joining links</small>
          </span>
        </summary>
        {sessions.length ? (
          <ul className="challenge-next__sessions">
            {sessions.map((session, index) => {
              const links = buildChallengeCalendarLinks(session, apiUrl);
              const youtubeUrl = youtubeLiveUrl(session);
              return (
                <li key={session.id}>
                  <div className="challenge-next__session-title">
                    <strong>
                      Part {index + 1}: {session.title}
                    </strong>
                    <time dateTime={session.scheduledAt}>
                      {formatChallengeDateTime(session.scheduledAt)}
                    </time>
                  </div>
                  <div className="challenge-next__session-actions">
                    {links ? (
                      <>
                        <a
                          className="challenge-next__action challenge-next__action--primary"
                          href={links.google}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Google Calendar
                        </a>
                        <a className="challenge-next__action" href={links.ics} download>
                          Apple or Outlook
                        </a>
                      </>
                    ) : null}
                    {session.joinUrl ? (
                      <a
                        className="challenge-next__action"
                        href={session.joinUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Join on Zoom
                      </a>
                    ) : null}
                    {youtubeUrl ? (
                      <a
                        className="challenge-next__action"
                        href={youtubeUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Watch on YouTube
                      </a>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="challenge-next__status">
            {scheduleError
              ? 'The live schedule is unavailable here. Check your confirmation email for your joining details.'
              : 'Loading your live schedule...'}
          </p>
        )}
      </details>
      <p className="challenge-next__bootcamp-footer">
        Prefer to explore on your own?{' '}
        <Link
          to={`/courses/ai-trading-bootcamp?utm_source=ai_trading_challenge&utm_medium=post_registration_footer&utm_campaign=${slug}`}
        >
          View the Bootcamp
        </Link>
      </p>
      <Link className="challenge-next__skip" to={`/challenges/${slug}`}>
        Back to the challenge
      </Link>
    </main>
  );
}
